import { setCookie, destroyCookie } from 'nookies'
import { locales } from '../../i18n.config'
import { getLocaleFromHref } from './url'
import { Country } from '@afterpay/types'
import { app } from '../env'

export const COOKIE_SHOW_NEW_PORTAL = 'x-apt-new-portal'
export const COOKIE_SHOW_OLD_PORTAL = 'x-apt-old-portal'

const setCookieToShowOldPortal = (): void => {
  setCookie(null, COOKIE_SHOW_OLD_PORTAL, JSON.stringify(true), {
    maxAge: 60 * 60, // 1 hour
    sameSite: 'lax',
    path: '/',
  })
}

export const getOldPortalUrl = (destination: string = ''): string => {
  // Redirect to old portal at origin with new locale mapped to old locale and any deeplinks applied
  const baseUrl: string = window.location.origin
  const currentLocale = getLocaleFromHref(window.location.href)
  // @ts-ignore: OPERATION BLEED STOPPER
  const oldLocale: string = locales.find((l) => l.locale === currentLocale).redirect
  const validDestination = destination !== '' ? '/' + destination : ''
  return `${baseUrl}/${oldLocale}${validDestination}`
}

export const navigateToShop = (): void => {
  window.location.href = app.SHOP_URL
}

export const switchToOldPortal = (destination: string = ''): void => {
  // Delete Cloudflare cookie
  destroyCookie(null, COOKIE_SHOW_NEW_PORTAL, { path: '/' })
  destroyCookie(null, COOKIE_SHOW_NEW_PORTAL, { path: '/', domain: window.location.hostname })
  setCookieToShowOldPortal()
  const oldPortalUrl = getOldPortalUrl(destination)
  window.location.replace(oldPortalUrl)
}

// TODO review these func, clean code while other region migration finished
// NOTE: We hardcode portal URLs here instead of using PORTAL_BASE_URL environment variable
// because this function handles cross-region login redirects. The PORTAL_BASE_URL contains
// the current region's URL, but we need to redirect users to different regional portals
// based on their country (e.g., GB users → clearpay.com, US users → afterpay.com).
//
// Additionally, we use hardcoded URLs because the old and new portals have different
// locale formats:
// - New portal: /en-AU, /en-US, /en-GB (standard locale format)
// - Old portal: /uk/login-email, /au/login-email, /nz/login-email (custom paths)
//
// Once the portal migration is complete, we can simplify this to use standard
// en-AU format URLs and potentially use environment variables.
export const getPortalUrlViaCountry = (country: Country): string => {
  const environment = app.APP_ENVIRONMENT
  const isLocal = /local/i.test(environment) || environment === 'development'
  const isQA = /(qa|sbox)/i.test(environment)
  const isStaging = /stg/i.test(environment)
  if (isLocal) {
    return `http://localhost:8090/en-${country}`
  }
  if (isQA) {
    switch (country) {
      case 'GB':
        return 'https://portal.qa.clearpay-beta.co.uk/uk/login-email'
      case 'US':
        return 'https://portal.qa.afterpay-beta.com/en-US'
      case 'NZ':
        return 'https://portal.qa.afterpay-beta.com/nz/login-email'
      case 'CA':
        return 'https://portal.qa.afterpay-beta.com/en-CA'
      case 'FR':
        return 'https://portal.qa.clearpay-beta.co.uk/fr/login-email'
      case 'ES':
        return 'https://portal.qa.clearpay-beta.co.uk/es/login-emaill'
      case 'IT':
        return 'https://portal.qa.clearpay-beta.co.uk/it/login-email/'
      default:
        return 'https://portal.qa.afterpay-beta.com/au/login-email'
    }
  }
  if (isStaging) {
    switch (country) {
      case 'GB':
        return 'https://portal.stage.clearpay-beta.co.uk/uk/login-email'
      case 'US':
        return 'https://portal.stage.afterpay-beta.com/en-US'
      case 'NZ':
        return 'https://portal.stage.afterpay-beta.com/nz/login-email'
      case 'CA':
        return 'https://portal.stage.afterpay-beta.com/en-CA'
      case 'FR':
        return 'https://portal.stage.clearpay-beta.co.uk/fr/login-email'
      case 'ES':
        return 'https://portal.stage.clearpay-beta.co.uk/es/login-email'
      case 'IT':
        return 'https://portal.stage.clearpay-beta.co.uk/it/login-email'
      default:
        return 'https://portal.stage.afterpay-beta.com/au/login-email'
    }
  }
  // Production (default fallback)
  switch (country) {
    case 'GB':
      return 'https://portal.clearpay.com/uk/login-email'
    case 'US':
      return 'https://portal.afterpay.com/en-US'
    case 'NZ':
      return 'https://portal.afterpay.com/nz/login-email'
    case 'CA':
      return 'https://portal.afterpay.com/en-CA'
    case 'FR':
      return 'https://portal.afterpay.com/fr/login-email'
    case 'ES':
      return 'https://portal.afterpay.com/es/login-email'
    case 'IT':
      return 'https://portal.afterpay.com/it/login-email'
    default:
      return 'https://portal.afterpay.com/au/login-email'
  }
}

export const getCashAppAppletUrl = (): string => {
  return getCashAppLink('/launch/afterpay-applet/home')
}

const CASH_APP_URL_FALLBACK = 'https://cash.app'

export const getCashAppLink = (path?: string, params?: Record<string, string>): string => {
  const base = app.CASH_APP_URL !== undefined && app.CASH_APP_URL !== '' ? app.CASH_APP_URL : CASH_APP_URL_FALLBACK
  const url = new URL(path ?? '', base)

  const paramsStr = new URLSearchParams(params).toString()
  url.search = paramsStr

  return url.toString()
}
